<template>
  <div :data-test-id="dataTestId" :class="outerClass" :style="inlineStyle">
    <template v-if="resolvedHeading?.value">
      <PanelTitle
        :title="resolvedHeading"
        :subTitle="{ value: undefined }"
        :panelType="{ value: PanelTitleTypes.Subheading }"
        :bindings="{ outerClass: 'mb-2' }"
      />
    </template>
    <div :class="contentGridClasses">
      <div
        v-for="(promoCard, index) in displayedPromoCards"
        :key="index"
        :data-test-id="promoCard.dataTestId?.value || dataTestId + '_' + index"
      >
        <PromoCardBasic v-bind="promoCard" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IPromoCardBasic } from '@/interfaces/entities/promo-card-basic.interface';
import { PanelTitleTypes } from '@/enums/panel-title-type.enum';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import { BasicCardLayout } from '@/enums/card-layout.enum';
import type { Field, ComponentFields, ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import PromoCardBasic from './PromoCardBasic.vue';
import { mapFieldToEnum } from '@/mappers/sc-field-to-enum.mapper';
import { usePageStore } from '@/store';

const props = defineProps<{
  bindings?: Record<string, any>;
  promoCards?: Partial<IPromoCardBasic>[];
  heading?: Field<string>;
  cardLayout?: Field<string>;
  maxItemsToShow?: Field<number>;
  fields?: ComponentFields;
  rendering?: ComponentRendering;
}>();

const outerClass = props.rendering?.params?.Styles;
const inlineStyle = props.rendering?.params?.inlineStyle;
const dataTestId = usePageStore().dataTestId(props.rendering);

const defaultMaxSupportedPromoCards = 6;

const resolvedPromoCards = computed(() => {
  if (props.promoCards?.length) {
    return props.promoCards;
  }

  const fieldItems = props.fields?.items;

  if (Array.isArray(fieldItems)) {
    return fieldItems.map((item) => item.fields as Partial<IPromoCardBasic>).filter((fields) => !!fields);
  }

  return [];
});

const resolvedHeading = computed(() => {
  return props.heading ?? (props.fields?.heading as Field<string>);
});

const resolvedCardLayoutField = computed(() => {
  return props.cardLayout ?? (props.fields?.cardLayout as Field<string>);
});

const resolvedCardLayout = computed<BasicCardLayout | undefined>(() =>
  mapFieldToEnum(BasicCardLayout, resolvedCardLayoutField.value)
);

const resolvedMaxItemsToShow = computed(() => {
  return (
    props.maxItemsToShow?.value ??
    (props.fields?.maxItemsToShow as Field<number>)?.value ??
    defaultMaxSupportedPromoCards
  );
});

const displayedPromoCards = computed(() => resolvedPromoCards.value.slice(0, resolvedMaxItemsToShow.value));

const contentGridClasses = computed(() => {
  if (resolvedCardLayout.value === BasicCardLayout.TWO_COLUMN) {
    return 'grid grid-cols-1 md:grid-cols-2 gap-y-6 md:gap-x-6';
  } else if (resolvedCardLayout.value === BasicCardLayout.THREE_COLUMN) {
    return 'grid grid-cols-[repeat(auto-fill,_minmax(296px,_1fr))] gap-y-6 md:gap-x-6';
  }
  return '';
});
</script>
